import { useState, useMemo } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Calculator, ArrowLeft, KeyRound, CheckCircle2 } from "lucide-react";
import api, { describeAxiosError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";

/** Token-link based password reset (Step 2).
 *
 * URL: `/reset-password?token=<256-bit-url-safe-token>`
 * The token is opaque to the frontend — we just pass it back to the
 * backend along with the new password. The backend hashes (SHA-256),
 * looks up the user, checks the 1-hour expiry, and invalidates the
 * token on successful reset.
 *
 * If `?token=` is missing or empty, we show a clear "link invalid"
 * state rather than silently 400-ing on submit.
 */
export default function ResetPassword() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => (params.get("token") || "").trim(), [params]);

  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e?.preventDefault();
    if (!token) {
      toast.error("This reset link is missing the token. Please request a new one.");
      return;
    }
    if (newPwd.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error("Passwords don't match");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: newPwd });
      toast.success("Password updated — please sign in with your new password");
      navigate("/login");
    } catch (err) {
      toast.error(describeAxiosError(err, "Reset failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50 grid place-items-center p-4">
      <div className="max-w-md w-full">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-10 h-10 rounded bg-blue-700 grid place-items-center">
            <Calculator className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-xl font-bold text-gray-900">AXIORA</div>
            <div className="text-xs text-gray-500">Choose a new password</div>
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg bg-white shadow-sm p-6">
          {!token && (
            <div className="space-y-4" data-testid="reset-token-missing">
              <div className="flex items-center gap-2 text-amber-700">
                <KeyRound className="w-4 h-4" />
                <h2 className="font-semibold">Reset link is invalid</h2>
              </div>
              <p className="text-sm text-gray-600">
                The link you opened is missing its security token. This usually
                happens when the URL was truncated by an email client. Please
                request a new reset link.
              </p>
              <Link to="/forgot-password">
                <Button className="w-full bg-blue-700 hover:bg-blue-800" data-testid="btn-request-new-link">
                  Request a new link
                </Button>
              </Link>
            </div>
          )}

          {token && (
            <form onSubmit={onSubmit} className="space-y-4" data-testid="form-reset-password">
              <div className="flex items-center gap-2 text-blue-700">
                <KeyRound className="w-4 h-4" />
                <h2 className="font-semibold">Set new password</h2>
              </div>
              <p className="text-sm text-gray-600">
                Pick a strong password you don&apos;t use on other sites. The
                reset link is valid for 1&nbsp;hour.
              </p>

              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider text-gray-600">New password</Label>
                <Input
                  type="password"
                  value={newPwd}
                  onChange={(e) => setNewPwd(e.target.value)}
                  autoComplete="new-password"
                  data-testid="input-new-password"
                  required
                  autoFocus
                />
                <p className="text-[11px] text-gray-500">Minimum 8 characters.</p>
              </div>

              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider text-gray-600">Confirm new password</Label>
                <Input
                  type="password"
                  value={confirmPwd}
                  onChange={(e) => setConfirmPwd(e.target.value)}
                  autoComplete="new-password"
                  data-testid="input-confirm-password"
                  required
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-blue-700 hover:bg-blue-800"
                data-testid="btn-submit-reset"
              >
                {submitting ? "Updating…" : "Update password"}
              </Button>

              <div className="pt-2 flex items-center justify-between text-xs">
                <Link to="/login" className="text-gray-500 hover:text-gray-900 inline-flex items-center gap-1" data-testid="link-back-login">
                  <ArrowLeft className="w-3 h-3" /> Back to sign in
                </Link>
                <span className="text-gray-400 inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Secure link
                </span>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
